import {
	HelmetDexteel,
	MenuOptionType,
	getMomentTz,
	useContextMenuMESF,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import AddBoxIcon from "@mui/icons-material/AddBox";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ListIcon from "@mui/icons-material/List";
import { Button, Grid2 as Grid, IconButton } from "@mui/material";
import {
	GridApi,
	ICellRendererParams,
	RowClassParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
	LinkButtonCellRenderer,
	ToggleActiveButtonCellRenderer,
	UnLinkButtonCellRenderer,
} from "../../../../controls/ag-grid/components/ButtonCellRenderer";
import { useGridDefinitions } from "../../../../controls/ag-grid/components/GridDefinitions";
import { ConfirmationDialogRaw } from "../../../../controls/modals/ConfirmationDialogRaw";
import { useStyles } from "../../caster-data-entry-v2/components/styles";
import { ProcessByShift } from "../../models/ProcessByShift";
import {
	deleteProcess,
	unDeleteProcess,
	unLinkProcess,
} from "../../repositories/ProductionRepository";
import { useGridStyles } from "../../styles/gridStyles";
import { useCasterProcessesContext } from "../CasterProcessesContext";
import { useOptionFunctions } from "../hooks/useOptionFunctions";
import { LinkCasterProcess } from "./LinkCasterProcess";
import { UpsertCasterProcess } from "./UpsertCasterProcess";
import NewProcessStatusBar from "./buttons/NewProcess";
import { useTableData } from "./hooks";

const moment = getMomentTz();

type Props = {
	loadingGrid: boolean;
	rows: ProcessByShift[];
	refreshData: Function;
};

export const CasterProcessesTable = ({
	loadingGrid,
	rows,
	refreshData,
}: Props) => {
	const classes = useGridStyles();
	const MUIclasses = useStyles();
	const gridRef = useRef<AgGridReact>(null);
	const [selectedProcessData, setSelectedProcessData] =
		useState<ProcessByShift | null>(null);
	const [gridAPI, setGridAPI] = useState<GridApi | null>(null);
	const [searchParams, setSearchParams] = useSearchParams();

	const [options, setOptions] = useState<MenuOptionType[]>([]);

	const {
		createData,
		editData,
		linkData,
		unlinkData,
		toggleStatusData,
		getMenuOptions,
		goToHeats,
		goToDataEntry,
		showActiveModal,
		showDeleteModal,
		showEditModal,
		showLinkModal,
		showUnlinkModal,
		messageActiveModal,
		messageDeleteModal,
		messageUnlinkModal,
		setShowActiveModal,
		setShowDeleteModal,
		setShowEditModal,
		setShowLinkModal,
		setShowUnlinkModal,
		setMessageActiveModal,
		setMessageDeleteModal,
		setMessageUnlinkModal,
	} = useOptionFunctions();

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { shiftInfo } = useShiftNavigator();
	const { columns } = useTableData({});

	const {
		state: {
			selectedProcess,
			anchorPoint: { x, y },
		},
		actions: {
			setSelectedProcess,
			setIsLoadingBackground,
			setNotificationMessage,
			setErrorMessage,
		},
	} = useCasterProcessesContext();

	const handleContainerContextMenu = (
		event: React.MouseEvent<HTMLDivElement, MouseEvent>,
	) => {
		event.preventDefault();
	};

	const onGridReady = (params: any) => {
		setGridAPI(params.api);
	};

	const onSelectionChanged = () => {
		if (!gridAPI) return;
		const selectedRows = gridAPI.getSelectedRows();
		if (selectedRows.length > 0) {
			setSelectedProcessData(selectedRows[0]);
			setSearchParams(
				{ processId: selectedRows[0].ProcessId },
				{ replace: true },
			);
		} else {
			setSelectedProcessData(null);
			setSearchParams({}, { replace: true });
		}
	};

	const getRowStyle = (params: RowClassParams) => {
		if (!params.data.IsActive) {
			return { background: "#E8EBEB" };
		}
	};

	const deleteCasterProcess = async (value?: string) => {
		setShowDeleteModal(false);
		setMessageDeleteModal("");
		if (value !== "OK") return;
		if (selectedProcess === null) return;
		setIsLoadingBackground(true);
		const resp = await deleteProcess(selectedProcess.ProcessId);
		if (resp.ok) {
			setIsLoadingBackground(false);
			setNotificationMessage("Process deleted successfully");
			await refreshData();
		} else {
			setIsLoadingBackground(false);
			setErrorMessage(resp.message);
		}
	};
	const activeCasterProcess = async (value?: string) => {
		setShowActiveModal(false);
		setMessageActiveModal("");
		if (value !== "OK") return;
		if (selectedProcess === null) return;
		setIsLoadingBackground(true);
		const resp = await unDeleteProcess(selectedProcess.ProcessId);
		if (resp.ok) {
			setIsLoadingBackground(false);
			setNotificationMessage("Process activated successfully");
			await refreshData();
		} else {
			setIsLoadingBackground(false);
			setErrorMessage(resp.message);
		}
	};
	const unlinkCasterProcess = async (value?: string) => {
		setShowUnlinkModal(false);
		setMessageUnlinkModal("");
		if (value !== "OK") return;
		if (selectedProcess === null) return;
		setIsLoadingBackground(true);
		const resp = await unLinkProcess(selectedProcess.ProcessId);
		if (resp.ok) {
			setIsLoadingBackground(false);
			setNotificationMessage("Process unlinked successfully");
			await refreshData();
		} else {
			setIsLoadingBackground(false);
			setErrorMessage(resp.message);
		}
	};

	const { columnSimpleDefaults, columnTypes } = useGridDefinitions({
		OnEdit: editData,
	});
	columnTypes["linkButton"] = {
		field: "Link",
		cellRenderer: LinkButtonCellRenderer,
		cellRendererParams: {
			clicked: linkData,
		},
		cellClass: "ag-button-cell",
	};
	columnTypes["unlinkButton"] = {
		field: "Unlink",
		cellRenderer: UnLinkButtonCellRenderer,
		cellRendererParams: {
			clicked: unlinkData,
		},
		cellClass: "ag-button-cell",
	};
	columnTypes["activeButton"] = {
		field: "IsActive",
		headerName: "",
		cellRenderer: ToggleActiveButtonCellRenderer,
		cellRendererParams: {
			clicked: toggleStatusData,
		},
		cellClass: "ag-button-cell",
	};
	columnTypes["optionsButton"] = {
		field: "Options",
		headerName: "",
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<div className={classes.buttons}>
					<IconButton
						aria-label="link/unlink"
						title={params.data.HeatName ? "Unlink" : "Link"}
						style={{ cursor: "pointer" }}
						onClick={() => {
							params.data.HeatName
								? unlinkData(params.data)
								: linkData(params.data);
						}}
					>
						{params.data.HeatName ? (
							<LinkOffIcon
								fontSize="inherit"
								color="primary"
								style={{ cursor: "pointer" }}
							/>
						) : (
							<LinkIcon
								fontSize="inherit"
								color="primary"
								style={{ cursor: "pointer" }}
							/>
						)}
					</IconButton>
					<IconButton
						aria-label="delete-icon"
						title={params.data.IsActive ? "Delete" : "Activate"}
						style={{ cursor: "pointer" }}
						onClick={() => toggleStatusData(params.data)}
					>
						{params.data.IsActive ? (
							<DeleteIcon
								fontSize="inherit"
								color="error"
								style={{ cursor: "pointer" }}
							/>
						) : (
							<AddBoxIcon
								fontSize="inherit"
								color="primary"
								style={{ cursor: "pointer" }}
							/>
						)}
					</IconButton>
					<IconButton
						aria-label="Options"
						title="Options"
						style={{ cursor: "pointer" }}
						onClick={(e) => showContextMenu(e, params.data, "casterProcesses")}
					>
						<ListIcon
							fontSize="inherit"
							color="primary"
							style={{ cursor: "pointer" }}
						/>
					</IconButton>
				</div>
			);
		},
		cellClass: classes.buttons,
	};

	const frameworkComponents = {
		newProcessStatusBar: NewProcessStatusBar,
	};

	const statusBarConfig = {
		statusPanels: [
			{
				statusPanel: "newProcessStatusBar",
				align: "right",
				statusPanelParams: { onNewProcess: createData },
			},
		],
	};

	useEffect(() => {
		if (
			loadingGrid &&
			gridRef.current !== null &&
			gridRef.current.api !== undefined
		) {
			gridRef.current!.api.showLoadingOverlay();
		} else if (gridRef.current !== null && gridRef.current.api !== undefined) {
			gridRef.current!.api.hideOverlay();
		}
	}, [loadingGrid]);

	useEffect(() => {
		if (!loadingGrid && gridAPI && rows && rows.length > 0) {
			const processId = searchParams.get("processId");

			if (processId) {
				const node = gridAPI.getRowNode(processId);
				if (node) {
					gridAPI.deselectAll();
					node?.setSelected(true, true);
					setSelectedProcessData(node.data);
				}
			}
		}
	}, [gridAPI, loadingGrid, rows]);

	useEffect(() => {
		registerConfig({
			id: "casterProcesses",
			getOptions: getMenuOptions,
		});
	}, [shiftInfo?.CurrentShiftId]);
	return (
		<>
			{selectedProcessData &&
				!showEditModal &&
				!showDeleteModal &&
				!showLinkModal &&
				!showUnlinkModal &&
				!showActiveModal && (
					<HelmetDexteel
						title={`${selectedProcessData.HeatName ? `${selectedProcessData.HeatName} - ` : ""}Caster Processes`}
					/>
				)}
			{showDeleteModal && <HelmetDexteel title={`Delete Process`} />}
			{showUnlinkModal && <HelmetDexteel title={`Unlink Process`} />}
			{showActiveModal && <HelmetDexteel title={`Activate Process`} />}
			<div onContextMenu={handleContainerContextMenu}>
				<Grid container>
					<Grid
						size={{ xs: 12, md: 12 }}
						style={{
							borderRadius: 10,
							height: "calc(100vh - 300px)",
							minHeight: "calc(100vh - 300px)",
							border: "1px solid #E0E0E0",
						}}
					>
						<AgGridReact
							ref={gridRef}
							onGridReady={onGridReady}
							rowSelection="single"
							pagination={false}
							animateRows={true}
							singleClickEdit={true}
							tooltipShowDelay={0}
							tooltipHideDelay={2000}
							rowData={rows}
							columnDefs={columns}
							columnTypes={columnTypes}
							getRowStyle={getRowStyle}
							getRowId={(params) => String(params.data.ProcessId)}
							suppressContextMenu={true}
							onRowDoubleClicked={(event: any) => {
								if (event.data.ProcessId) goToDataEntry(event.data.ProcessId);
							}}
							onCellContextMenu={(event) => {
								event.api.deselectAll();
								event.node.setSelected(true);
								setSelectedProcess(event?.data);

								if (event.event)
									showContextMenu(
										event.event as any,
										event.data,
										"casterProcesses",
									);
							}}
							onSelectionChanged={onSelectionChanged}
							statusBar={statusBarConfig}
							components={frameworkComponents}
						/>
					</Grid>
				</Grid>
				<ConfirmationDialogRaw
					title="Confirmation"
					message={messageDeleteModal}
					open={showDeleteModal}
					onClose={deleteCasterProcess}
				/>
				<ConfirmationDialogRaw
					title="Confirmation"
					message={messageActiveModal}
					open={showActiveModal}
					onClose={activeCasterProcess}
				/>
				<ConfirmationDialogRaw
					title="Confirmation"
					message={messageUnlinkModal}
					open={showUnlinkModal}
					onClose={unlinkCasterProcess}
				/>
				<UpsertCasterProcess
					show={showEditModal}
					onHide={() => setShowEditModal(false)}
					refreshData={refreshData}
				/>
				<LinkCasterProcess
					show={showLinkModal}
					onHide={async () => {
						setShowLinkModal(false);
						setNotificationMessage("Process linked successfully");
						await refreshData();
					}}
					process={selectedProcess}
				/>
			</div>
		</>
	);
};
